import * as React from 'react';
import {Mutation} from 'react-apollo';
// @ts-expect-error
import type {DataProxy} from 'react-apollo';
import {withRouter} from 'react-router-dom';
// @ts-expect-error
import type {ContextRouter, RouterHistory} from 'react-router-dom';

import {Link} from 'shells/link/link';

import type {
    DeleteEmailInput,
    DeleteEmail as DeleteEmailMutation,
    DeleteEmailVariables as DeleteEmailMutationVariables,
    UpdateEmail as UpdateEmailMutation,
    UpdateEmailVariables as UpdateEmailMutationVariables,
    EmailShareAndCreateContactsInput,
    RetryFailedEmail as RetryFailedEmailMutation,
    RetryFailedEmailVariables as RetryFailedEmailMutationVariables,
    FailedEmailRetryInput,
    Email as EmailFragment,
} from 'nutshell-graphql-types';

import {EmailRow} from '../email-row';
import DELETE_EMAIL from '../graphql/mutations/delete-email.graphql';
import RETRY_FAILED_EMAIL from '../graphql/mutations/retry-failed-email.graphql';
import UPDATE_EMAIL from '../graphql/mutations/update-email.graphql';
import {mapEmailFragmentToEmailMessage} from '../utils';

type OwnProps = {
    email: EmailFragment;
    isFirstOfDate: boolean;
    onEmailSharedChange: (
        proxy: DataProxy,
        update: {
            data?: UpdateEmailMutation;
        }
    ) => void;
    onEmailDeleted: (
        proxy: DataProxy,
        update: {
            data?: DeleteEmailMutation;
        }
    ) => void;
    onEmailRetried: (
        proxy: DataProxy,
        update: {
            data?: RetryFailedEmailMutation;
        }
    ) => void;
};

type State = {
    isDeletingEmail: boolean;
};

type Props = OwnProps & {
    history: RouterHistory;
    onDeleteEmail: (arg1: {
        payload: DeleteEmailInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: DeleteEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
    onUpdateEmail: (arg1: {
        payload: EmailShareAndCreateContactsInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: UpdateEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
    onRetryFailedEmail: (arg1: {
        payload: FailedEmailRetryInput;
        update: (
            proxy: DataProxy,
            update: {
                data?: RetryFailedEmailMutation;
            }
        ) => void;
    }) => Promise<any>;
};

class FailedEmailRowItemComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = {
            isDeletingEmail: false,
        };
    }

    render() {
        const email = mapEmailFragmentToEmailMessage(this.props.email);

        // We use the URL for the queued email as a link to the
        // whole sequence. And we only want to show it if there is
        // a sequence, and if there are multiple messages (else, why
        // view it as a sequence).
        const queuedEmailHtmlUrl =
            this.props.email.emailSequence &&
            this.props.email.emailSequence.messageCount > 1 &&
            this.props.email.queuedEmail
                ? this.props.email.queuedEmail.htmlUrl
                : undefined;

        return (
            <EmailRow
                key={this.props.email.id}
                emailListItem={email}
                isFirstOfDate={this.props.isFirstOfDate}
                status={
                    this.props.email.isShared
                        ? undefined
                        : {
                              level: 'hidden',
                              text: 'Private',
                              detail: 'This email is only visible to you',
                          }
                }
                sequenceInfo={getSequenceInfo(this.props.email)}
                isDeletingEmail={this.state.isDeletingEmail}
                onDeleteConfirm={this.handleDeleteConfirm}
                onDeleteCancel={this.handleDeleteCancel}
                // @ts-expect-error
                actions={this.getActionMenuOptions()}
                navToSequenceDetails={
                    queuedEmailHtmlUrl
                        ? () => {
                              this.props.history.push(queuedEmailHtmlUrl);
                          }
                        : undefined
                }
                detailLinkComponent={
                    <Link variant='secondary' href={email.htmlUrl}>
                        View details
                    </Link>
                }
            />
        );
    }

    getActionMenuOptions = () => {
        const actions = [];

        if (this.props.email.isShareable) {
            actions.push({
                label: this.props.email.isShared ? 'Unshare' : 'Share',
                handleSelect: () => {
                    this.props.onUpdateEmail({
                        payload: {
                            emailId: this.props.email.id,
                            shouldShare: !this.props.email.isShared,
                        },
                        update: this.props.onEmailSharedChange,
                    });
                },
            });
        }

        actions.push({
            label: 'Retry',
            handleSelect: () => {
                this.props.onRetryFailedEmail({
                    payload: {
                        emailId: this.props.email.id,
                    },
                    update: this.props.onEmailRetried,
                });
            },
        });

        actions.push({
            label: 'Delete',
            variant: 'destructive',
            handleSelect: () => this.setState({isDeletingEmail: true}),
        });

        return actions;
    };

    handleDeleteCancel = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({isDeletingEmail: false});
    };

    handleDeleteConfirm = (e: React.SyntheticEvent<any>) => {
        e.stopPropagation();
        this.setState({isDeletingEmail: false});

        this.props.onDeleteEmail({
            payload: {
                email: {
                    id: this.props.email.id,
                },
            },
            update: this.props.onEmailDeleted,
        });
    };
}

function getSequenceInfo(failedEmail: EmailFragment) {
    // For some reason, all emails from templates have an email sequence and corresponding
    // queued email, even through they're not part of a sequence, or ever queued. Therefore we
    // need to also check the sequence messgae count is more than 1.
    return failedEmail.emailSequence &&
        failedEmail.queuedEmail &&
        failedEmail.emailSequence.emailSequenceTemplate &&
        failedEmail.emailSequence.messageCount > 1
        ? {
              name: failedEmail.emailSequence.emailSequenceTemplate.name,
              messageNumber: failedEmail.queuedEmail.messageNumber,
              messageCount: failedEmail.emailSequence.messageCount,
          }
        : null;
}

class DeleteMutation extends Mutation<DeleteEmailMutation, DeleteEmailMutationVariables> {}
class UpdateMutation extends Mutation<UpdateEmailMutation, UpdateEmailMutationVariables> {}
class RetryMutation extends Mutation<RetryFailedEmailMutation, RetryFailedEmailMutationVariables> {}

const FailedEmailRowItemGraphQL = (props: ContextRouter & OwnProps) => (
    <DeleteMutation mutation={DELETE_EMAIL} refetchQueries={['GetEmailStats']}>
        {(deleteEmail) => (
            <UpdateMutation mutation={UPDATE_EMAIL}>
                {(updateEmail) => (
                    <RetryMutation mutation={RETRY_FAILED_EMAIL} refetchQueries={['GetEmailStats']}>
                        {(retryFailedEmail) => (
                            <FailedEmailRowItemComponent
                                onEmailSharedChange={props.onEmailSharedChange}
                                onEmailDeleted={props.onEmailDeleted}
                                onEmailRetried={props.onEmailRetried}
                                isFirstOfDate={props.isFirstOfDate}
                                email={props.email}
                                history={props.history}
                                onDeleteEmail={({payload, update}) =>
                                    deleteEmail({variables: {payload}, update})
                                }
                                onUpdateEmail={({payload, update}) =>
                                    updateEmail({variables: {payload}, update})
                                }
                                onRetryFailedEmail={({payload, update}) =>
                                    retryFailedEmail({variables: {payload}, update})
                                }
                            />
                        )}
                    </RetryMutation>
                )}
            </UpdateMutation>
        )}
    </DeleteMutation>
);

// @ts-expect-error
export const FailedEmailRowItem = withRouter<OwnProps>(FailedEmailRowItemGraphQL);
